import { FC, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { create } from 'zustand';

import { AnalyticsEvent, setUserProperty, track } from '@bonusly/analytics';

import { getSeasonConfig } from './get-season-config';
import { ACTIVE_SEASON } from './seasonal-config';
import { useSeasonalTheme } from './seasonal-theme-context';
import { SeasonalThemeModal } from './seasonal-theme-modal';

interface GlobalSeasonalThemeModalStore {
	isOpen: boolean;
	onOpen: () => void;
	onClose: () => void;
}

const useGlobalSeasonalThemeModal = create<GlobalSeasonalThemeModalStore>((set) => ({
	isOpen: false,
	onOpen: () => set({ isOpen: true }),
	onClose: () => set({ isOpen: false }),
}));

export const GlobalSeasonalThemeModal: FC = () => {
	const { isOpen, onClose } = useGlobalSeasonalThemeModal();
	const { hasSeasonalThemePermission, isSeasonalThemeEnabled, toggleTheme } = useSeasonalTheme();
	const [searchParams, setSearchParams] = useSearchParams();

	// Session state signal: record the theme's on/off state (and the falling
	// emoji preference, when the active season has one) as Amplitude user
	// properties. Events only capture explicit changes; these properties make
	// the silent majority measurable. seasonal_theme_permission identifies the
	// flagged population, so adoption = active(true) / permission(true).
	useEffect(() => {
		setUserProperty('seasonal_theme_permission', hasSeasonalThemePermission);
		setUserProperty('seasonal_theme_active', isSeasonalThemeEnabled);

		const toggleKey = getSeasonConfig().modalText.features.find((feature) => feature.toggleKey)?.toggleKey;
		if (toggleKey) {
			let fallingEmojisEnabled = true;
			try {
				const raw = localStorage.getItem(toggleKey);
				if (raw !== null) fallingEmojisEnabled = JSON.parse(raw) === true;
			} catch {
				// Unreadable value — report the default-on behavior the UI uses.
			}
			setUserProperty('falling_emojis_enabled', fallingEmojisEnabled);
		}
	}, [hasSeasonalThemePermission, isSeasonalThemeEnabled]);

	// Deep-link support: ?seasonal-theme-modal=true opens the modal directly on
	// any route (e.g. from the promo modal, announcements, or Bizy). The param
	// is consumed so refresh/back doesn't reopen the modal.
	const modalLinkParam = searchParams.get('seasonal-theme-modal');
	useEffect(() => {
		if (modalLinkParam === 'true' && hasSeasonalThemePermission) {
			track(AnalyticsEvent.SeasonalTheme.ModalLinkOpened, { season: ACTIVE_SEASON });
			useGlobalSeasonalThemeModal.getState().onOpen();
			const next = new URLSearchParams(searchParams);
			next.delete('seasonal-theme-modal');
			setSearchParams(next, { replace: true });
		}
	}, [modalLinkParam, hasSeasonalThemePermission, searchParams, setSearchParams]);

	if (!hasSeasonalThemePermission) {
		return null;
	}

	return <SeasonalThemeModal isOpen={isOpen} onClose={onClose} onEnable={toggleTheme} />;
};
